import {
  MARK_BOTTOM_TRACK,
  MARK_PATHS,
  MARK_TOP_TRACK,
  MARK_VIEW_BOX,
} from '@/components/brand/xendrel-geometry'

/**
 * Hero backdrop: the brand mark in outline as a slowly living system — two
 * signals travel its chevrons in toward the centre and out again.
 */
export function MarkField({ className }: { className?: string }) {
  return (
    <svg viewBox={MARK_VIEW_BOX} className={className} aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="9" strokeLinejoin="round">
        {MARK_PATHS.map(d => (
          <path key={d} d={d} />
        ))}
      </g>
      <g className="motion-reduce:hidden">
        <circle r="31" className="fill-primary">
          <animateMotion dur="16s" repeatCount="indefinite" path={MARK_TOP_TRACK} />
        </circle>
        <circle r="20" className="fill-foreground/50">
          <animateMotion dur="23s" repeatCount="indefinite" path={MARK_BOTTOM_TRACK} />
        </circle>
      </g>
    </svg>
  )
}

/** Schema definition flowing into a typed graph — section 01. */
export function SchemaFlowVisual({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 360 220" className={className} aria-hidden="true" focusable="false">
      {/* Schema card: the shapes the user defines */}
      <g className="text-border" stroke="currentColor" fill="none">
        <rect x="12" y="24" width="128" height="172" rx="10" />
        <rect x="28" y="44" width="96" height="26" rx="6" />
        <rect x="28" y="82" width="96" height="26" rx="6" />
        <rect x="28" y="120" width="96" height="26" rx="6" />
        <rect x="28" y="158" width="96" height="26" rx="6" strokeDasharray="4 3" />
      </g>
      <g className="fill-muted-foreground font-mono" fontSize="9" letterSpacing="1.5">
        <text x="38" y="61">
          PERSON
        </text>
        <text x="38" y="99">
          COMPANY
        </text>
        <text x="38" y="137">
          PAYMENT
        </text>
        <text x="38" y="175">
          PAID_TO
        </text>
      </g>
      {/* Flow arrow */}
      <g className="text-muted-foreground" stroke="currentColor" fill="none">
        <path d="M 152 110 H 196" strokeDasharray="5 4" />
        <path d="M 190 104 L 198 110 L 190 116" />
      </g>
      {/* Resulting typed graph */}
      <g className="text-border" stroke="currentColor">
        <path d="M 252 64 L 310 96" />
        <path d="M 252 64 L 232 140" />
        <path d="M 232 140 L 310 96" />
        <path d="M 310 96 L 330 164" />
        <path d="M 232 140 L 296 178" />
      </g>
      <g>
        <circle cx="252" cy="64" r="9" className="fill-primary" />
        <circle cx="310" cy="96" r="7" className="fill-foreground/70" />
        <circle cx="232" cy="140" r="7" className="fill-foreground/70" />
        <circle cx="330" cy="164" r="5" className="fill-muted-foreground" />
        <circle cx="296" cy="178" r="4" className="fill-muted-foreground" />
      </g>
    </svg>
  )
}

/** Depth: a document distilled into connected entities — section 02, left panel. */
export function DepthVisual({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 220 130" className={className} aria-hidden="true" focusable="false">
      <g className="text-border" stroke="currentColor" fill="none">
        <path d="M 24 18 H 74 L 90 34 V 112 H 24 Z" />
        <path d="M 74 18 V 34 H 90" />
        <path d="M 36 48 H 78 M 36 62 H 78 M 36 76 H 66" />
      </g>
      <g className="text-muted-foreground" stroke="currentColor" fill="none">
        <path d="M 104 65 H 128" strokeDasharray="4 3" />
      </g>
      <g className="text-border" stroke="currentColor">
        <path d="M 158 42 L 192 66" />
        <path d="M 158 42 L 146 96" />
        <path d="M 146 96 L 192 66" />
      </g>
      <circle cx="158" cy="42" r="8" className="fill-primary" />
      <circle cx="192" cy="66" r="6" className="fill-foreground/70" />
      <circle cx="146" cy="96" r="6" className="fill-foreground/70" />
    </svg>
  )
}

/** Breadth: everything chunked and vectorized, hits lighting up — section 02, right panel. */
export function BreadthVisual({ className }: { className?: string }) {
  const cols = [0, 1, 2, 3, 4, 5]
  const rows = [0, 1, 2, 3]
  const hits = new Set(['1-2', '3-0', '4-3'])
  return (
    <svg viewBox="0 0 220 130" className={className} aria-hidden="true" focusable="false">
      {rows.map(r =>
        cols.map(c => {
          const hit = hits.has(`${c}-${r}`)
          return (
            <rect
              key={`${c}-${r}`}
              x={26 + c * 30}
              y={22 + r * 24}
              width="22"
              height="14"
              rx="3"
              className={hit ? 'fill-primary' : 'fill-foreground/15'}
            />
          )
        })
      )}
    </svg>
  )
}

/** Views triptych thumbnails — section 08. */
export function GraphThumb({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 120" className={className} aria-hidden="true" focusable="false">
      <g className="text-border" stroke="currentColor">
        <path d="M 100 34 L 54 66 M 100 34 L 148 58 M 54 66 L 92 96 M 148 58 L 92 96 M 148 58 L 172 92 M 54 66 L 28 88" />
      </g>
      <circle cx="100" cy="34" r="9" className="fill-primary" />
      <circle cx="54" cy="66" r="6" className="fill-foreground/70" />
      <circle cx="148" cy="58" r="7" className="fill-foreground/70" />
      <circle cx="92" cy="96" r="5" className="fill-muted-foreground" />
      <circle cx="172" cy="92" r="4" className="fill-muted-foreground" />
      <circle cx="28" cy="88" r="4" className="fill-muted-foreground" />
    </svg>
  )
}

export function TimelineThumb({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 120" className={className} aria-hidden="true" focusable="false">
      <rect x="76" y="30" width="52" height="60" rx="6" className="fill-primary/10" />
      <g className="text-border" stroke="currentColor">
        <path d="M 16 60 H 184" />
        <path d="M 32 54 V 66 M 62 54 V 66 M 92 54 V 66 M 122 54 V 66 M 152 54 V 66" />
      </g>
      <circle cx="62" cy="60" r="5" className="fill-foreground/70" />
      <circle cx="92" cy="60" r="6" className="fill-primary" />
      <circle cx="122" cy="60" r="5" className="fill-foreground/70" />
      <circle cx="152" cy="60" r="4" className="fill-muted-foreground" />
    </svg>
  )
}

/** Movement track shared by the visible path and its animated live position. */
const MAP_TRACK = 'M 24 96 C 56 78 60 46 96 52 C 120 56 128 62 142 66'

export function MapThumb({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 120" className={className} aria-hidden="true" focusable="false">
      <g className="text-border" stroke="currentColor" fill="none">
        <path d="M 20 30 H 180 M 20 60 H 180 M 20 90 H 180" strokeDasharray="1 6" />
      </g>
      {/* Watched zone */}
      <path
        d="M 118 34 L 172 44 L 164 92 L 112 84 Z"
        className="fill-primary/10 text-primary"
        stroke="currentColor"
        strokeDasharray="5 4"
      />
      {/* Movement track with live position */}
      <path d={MAP_TRACK} className="text-foreground/60" stroke="currentColor" fill="none" />
      <circle r="4" className="fill-primary motion-reduce:hidden">
        <animateMotion dur="7s" repeatCount="indefinite" path={MAP_TRACK} />
      </circle>
      <circle cx="142" cy="66" r="3" className="fill-foreground/50" />
    </svg>
  )
}

/** Conversational assistant: question bubble, cited answer bubble — section 05. */
export function AssistantVisual({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 220 130" className={className} aria-hidden="true" focusable="false">
      <g className="text-border" stroke="currentColor" fill="none">
        <rect x="24" y="20" width="96" height="30" rx="12" />
      </g>
      <path d="M 40 35 H 104" className="text-muted-foreground" stroke="currentColor" />
      <rect
        x="76"
        y="64"
        width="120"
        height="42"
        rx="12"
        className="fill-primary/10 text-primary"
        stroke="currentColor"
      />
      <g className="text-muted-foreground" stroke="currentColor">
        <path d="M 92 79 H 180 M 92 91 H 146" />
      </g>
      {/* Citation chip on the answer */}
      <rect x="158" y="86" width="22" height="10" rx="3" className="fill-primary" />
    </svg>
  )
}

/** One outbound lane: gateway mouth (156,110) curving to a plate's left edge. */
const lanePath = (y: number) => `M 156 110 C 182 110 186 ${y} 206 ${y}`

/** The four outbound lanes, fanned from the gateway to the source plates. */
const OSINT_LANES = [
  { y: 44, label: 'COMPANY REGISTERS' },
  { y: 88, label: 'INSOLVENCY & DEBT' },
  { y: 132, label: 'SANCTIONS LISTS' },
  { y: 176, label: 'OPEN WEB' },
]

/**
 * OSINT reach: a sealed perimeter with exactly one gateway, four outbound lanes
 * to public sources, and one node inside lit up as what came back — section 06.
 */
export function OsintReachVisual({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 360 220" className={className} aria-hidden="true" focusable="false">
      {/* The perimeter everything else on this page stays inside */}
      <rect
        x="6"
        y="30"
        width="140"
        height="160"
        rx="10"
        className="text-border"
        stroke="currentColor"
        strokeDasharray="5 4"
        fill="none"
      />
      <text
        x="8"
        y="22"
        className="fill-muted-foreground font-mono"
        fontSize="8"
        letterSpacing="1.6"
      >
        YOUR NETWORK
      </text>

      {/* Local graph — the primary node is what an OSINT run brought home */}
      <g className="text-border" stroke="currentColor">
        <path d="M 46 72 L 100 96 M 46 72 L 62 140 M 62 140 L 100 96 M 100 96 L 108 158" />
      </g>
      <circle cx="46" cy="72" r="7" className="fill-foreground/70" />
      <circle cx="100" cy="96" r="6" className="fill-foreground/70" />
      <circle cx="62" cy="140" r="5" className="fill-muted-foreground" />
      <circle cx="108" cy="158" r="6" className="fill-primary" />

      {/* The one door that opens outward */}
      <rect x="138" y="98" width="16" height="24" rx="3" className="fill-primary" />

      <g className="text-border" stroke="currentColor" fill="none">
        {OSINT_LANES.map(lane => (
          <path key={lane.y} d={lanePath(lane.y)} strokeDasharray="4 3" />
        ))}
      </g>

      {/* Source plates */}
      {OSINT_LANES.map(lane => (
        <g key={lane.label}>
          <rect
            x="206"
            y={lane.y - 16}
            width="148"
            height="32"
            rx="8"
            className="text-border"
            stroke="currentColor"
            fill="none"
          />
          <circle cx="206" cy={lane.y} r="2.5" className="fill-primary" />
          <text
            x="222"
            y={lane.y + 3}
            className="fill-muted-foreground font-mono"
            fontSize="8"
            letterSpacing="0.9"
          >
            {lane.label}
          </text>
        </g>
      ))}

      {/* Live traffic on two of the lanes */}
      <g className="motion-reduce:hidden">
        <circle r="2.5" className="fill-primary">
          <animateMotion dur="4s" repeatCount="indefinite" path={lanePath(OSINT_LANES[0].y)} />
        </circle>
        <circle r="2" className="fill-primary/70">
          <animateMotion dur="5.5s" repeatCount="indefinite" path={lanePath(OSINT_LANES[2].y)} />
        </circle>
      </g>
    </svg>
  )
}

/** Deep research: sources examined, findings assembled — section 05. */
export function ResearchVisual({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 220 130" className={className} aria-hidden="true" focusable="false">
      <g className="text-border" stroke="currentColor" fill="none">
        <path d="M 24 18 H 66 L 80 32 V 112 H 24 Z" />
        <path d="M 66 18 V 32 H 80" />
        <path d="M 34 46 H 70 M 34 58 H 70 M 34 70 H 58" />
      </g>
      {/* Magnifier over the source */}
      <g className="text-primary" stroke="currentColor" fill="none">
        <circle cx="76" cy="78" r="14" />
        <path d="M 86 88 L 98 100" />
      </g>
      {/* Findings assembling on the right */}
      {[34, 58, 82].map(y => (
        <g key={y}>
          <circle cx="124" cy={y} r="3" className="fill-primary" />
          <rect x="134" y={y - 5} width="62" height="10" rx="3" className="fill-foreground/15" />
        </g>
      ))}
    </svg>
  )
}
